import { Box, Stack } from "@mui/material";
import * as Sentry from "@sentry/react";
import type { UseMutationResult } from "@tanstack/react-query";
import { createContext, useContext, useEffect } from "react";
import { apiHooks, type Schemas } from "../apis/norsk-gassnett-api-hooks";
import Loader from "../components/atomics/loader";
import { useAuthContext } from "./auth-context";

export type IdentityContextType = {
	identity: Schemas["MeResponseBody"]["current"];
	linked: Schemas["MeResponseBody"]["linked"];
	isLng: boolean;

	refetchIdentity: () => Promise<unknown>;
	identityMutation: UseMutationResult<Schemas["MeResponseBody"], Error>;
};

const IdentityContext = createContext<IdentityContextType>(
	{} as IdentityContextType,
);

export function IdentityProvider({ children }: { children: React.ReactNode }) {
	const { token } = useAuthContext();

	const identity = apiHooks.useQuery(
		"get",
		"/v1/identity/me",
		{
			headers: { Authorization: `Bearer ${token}` },
		},
		{ enabled: !!token, retry: false, refetchOnWindowFocus: false },
	);

	useEffect(() => {
		if (identity.data?.data?.current?.id) {
			Sentry.setUser({ id: identity.data.data.current.id });
		}
	}, [identity]);

	if (!identity.data?.data.current) {
		return (
			<Box
				sx={{
					display: "flex",
					minHeight: "100vh",
					flexDirection: "column",
				}}
			>
				<Stack
					justifyContent={"center"}
					alignItems="center"
					direction="column"
					sx={{ width: "100%", flexGrow: 1 }}
				>
					<Loader variant="container" />
				</Stack>
			</Box>
		);
	}

	return (
		<IdentityContext.Provider
			value={{
				identity: identity.data.data.current,
				linked: identity.data.data.linked,
				isLng: identity.data.data.current.departmentCode
					.toLowerCase()
					.includes("lng"),
				refetchIdentity: identity.refetch,
				// @ts-expect-error We are expecting this as the type is not 100% correct, but that is fine
				identityMutation: identity,
			}}
		>
			{children}
		</IdentityContext.Provider>
	);
}

export const useIdentityContext = () => useContext(IdentityContext);
